import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { Table, Popconfirm, Button, Flex, message, Pagination, Input, Select, Space, Spin, Avatar, Typography } from "antd";
import { useState, useEffect } from "react";
import { createAIArticleCategory, deleteAIArticleCategory, fetchAIArticleCategorys, updateAIArticleCategory } from "../../api/ai";
import UserInfoView from "../../components/community/user_info_view";
import { useDebouncedCallback } from "use-debounce";
import { fetchPostCreator } from "../../api/community";

const AIArticleCategoryScreen = () => {
    const [tabData, setTabData] = useState([]);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    const tabCols = [
        {
            title: 'Index',
            dataIndex: 'id',
            key: 'id',
            render: (text, _, index) => ((pageNum - 1) * pageSize + index + 1)
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record, index) => {
                if (record.isEditing) {
                    return <Input
                        allowClear
                        style={{ maxWidth: 200, fontSize: 12 }}
                        value={record.name}
                        placeholder="please input filter"
                        onChange={evt => onInputChange(evt.target.value, index, 'name')}
                    />;
                }
                return text;
            }
        },
        {
            title: 'Weight',
            dataIndex: 'weight',
            key: 'weight',
            render: (text, record, index) => {
                if (record.isEditing) {
                    return <Input
                        style={{ maxWidth: 200, fontSize: 12 }}
                        value={record.weight}
                        placeholder="weight"
                        type="number"
                        onChange={evt => onInputChange(evt.target.value, index, 'weight')}
                    />;
                }
                return text;
            }
        },
        {
            title: 'Assigned Author',
            dataIndex: 'user',
            key: 'user',
            width: 300,
            render: (text, record, index) => {
                if (record.isEditing) {
                    return <Select
                        style={{ width: '100%' }}
                        value={record.userId}
                        placeholder='search user'
                        defaultActiveFirstOption={false}
                        suffixIcon={null}
                        filterOption={false}
                        onSearch={handleUserSearch}
                        notFoundContent={searchLoading ? <Spin /> : null}
                        options={users}
                        optionRender={(option) => {
                            return (
                                <Space>
                                    {
                                        option.data.avatar ?
                                            <Avatar size={32} src={option.data.avatar} />
                                            :
                                            <Avatar size={32} style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
                                    }
                                    <div>
                                        <b>{option.data.label ? option.data.label : 'anonymous'}</b>
                                        <div>
                                            <Typography.Text style={{ color: '#999', fontSize: 12 }}>{option.data.email ?? option.data.value}</Typography.Text>
                                        </div>
                                    </div>
                                </Space>
                            )
                        }}
                        onChange={value => onInputChange(value, index, 'userId')}
                        allowClear
                        showSearch
                    />;
                }
                if (record.user?.userId) return <UserInfoView user={record.user} />
                return null;
            }
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (text, record) => {
                return (
                    record.isEditing ?
                        <Button.Group size='small'>
                            <PermissionControl permission="ainewscategory.control.saveEdit.e26712cf" variant={(record.id && record.id !== '-1') ? 'update' : 'create'}><Button type='link' loading={record.isLoading} onClick={() => saveEdit(record)}>Save</Button></PermissionControl>
                            <PermissionControl permission="ainewscategory.control.cancelEdit.8b8166f1"><Button type='link' onClick={() => cancelEdit(record)}><label style={{ color: '#afafaf', cursor: 'pointer' }}>Cancel</label></Button></PermissionControl>
                        </Button.Group>
                        :
                        <Button.Group size='small'>
                            <PermissionControl permission="ainewscategory.control.editCategory.86c41aef"><Button type='link' onClick={() => editCategory(record)}>Edit</Button></PermissionControl>
                            <PermissionControl permission="ainewscategory.control.deleteCategory.21d492a9"><Popconfirm
                                title="Delete"
                                description="Are you sure to delete this category?"
                                onConfirm={() => deleteCategory(record, [record.id])}
                                okText="Yes"
                                cancelText="No" >
                                <Button danger type='link' loading={record.isLoading}>Delete</Button>
                            </Popconfirm></PermissionControl>
                        </Button.Group>
                )
            }
        }
    ];

    useEffect(() => {
        fetchData(pageNum, pageSize);
        handleUserSearch('');
    }, [])

    const fetchData = (pNum, pSize) => {
        setLoading(true);
        let params = {
            'page.num': pNum,
            'page.size': pSize,
        };
        fetchAIArticleCategorys(params).then(res => {
            if (res.page) {
                setTotal(res.page.total);
            }
            if (res.list) {
                setTabData(res.list.map(item => {
                    return {
                        ...item,
                        isEditing: false,
                        isLoading: false,
                        selectedIds: []
                    };
                }));
            }
        }).finally(() => setLoading(false)).catch(showRequestError)
    }

    const pageChange = (page, size) => {
        setPageNum(page);
        setPageSize(size);
        fetchData(page, size);
    }

    const editCategory = (item) => {
        item.isEditing = true;
        setTabData([...tabData]);
    }

    const deleteCategory = (category, ids) => {
        category.isLoading = true;
        setTabData([...tabData]);
        let reqArr = [];
        ids.forEach(item => {
            reqArr.push(deleteAIArticleCategory(item));
        })

        Promise.all(reqArr).then(res => {
            if (res.some(item => item)) {
                fetchData(pageNum, pageSize);
            }
        }).catch(showRequestError)
    }

    const handleUserSearch = useDebouncedCallback(
        (keyword) => {
            let params = {
                'page.num': 1,
                'page.size': 10,
                'name': keyword,
            }
            setSearchLoading(true);
            fetchPostCreator(params).then(res => {
                if (res.list) {
                    let tmp = res.list.map(item => ({
                        avatar: (item.picture ? item.picture : item.avatar),
                        email: item.email,
                        label: item.name,
                        value: item.userId,
                    }));
                    setUsers(tmp);
                }
            }).finally(() => setSearchLoading(false)).catch(showRequestError)
        },
        300
    )

    const onInputChange = (value, index, key) => {
        tabData[index][key] = value;
        setTabData([...tabData]);
    }

    const saveEdit = (item) => {
        let canSubmit = String(item.name).trim();
        if (!canSubmit) {
            message.warning('Category name can not be empty!');
            return;
        }
        item.isLoading = true;
        setTabData([...tabData]);

        let newItem = { ...item };
        if (newItem.id === '-1') delete newItem['id'];

        let reqArr = [];
        if (newItem.id) {
            reqArr.push(updateAIArticleCategory({id: newItem.id, name: newItem.name, weight: newItem.weight, userId: newItem.userId}));
        } else {
            reqArr.push(createAIArticleCategory({name: newItem.name, weight: newItem.weight, userId: newItem.userId}));
        }

        Promise.all(reqArr).then(res => {
            fetchData(pageNum, pageSize);
        }).finally(() => {
            setLoading(false);
        }).catch(showRequestError);
    }

    const cancelEdit = (item) => {
        item.isEditing = false;
        let newData = [...tabData].filter(cgr => cgr.id !== '-1');
        setTabData(newData);
    }

    const addNewCategory = () => {
        setTabData([
            {
                name: '',
                weight: 0,
                userId: null,
                id: '-1',
                isEditing: true,
            }, ...tabData
        ]);
    }

    return (
        <div className='main-wrapper'>
            <div className='top-part cnt-item'>
                <Flex justify='space-between'>
                    <PermissionControl permission="ainewscategory.control.button.ce5ac8db"><Button type='primary' icon={<PlusOutlined />} onClick={addNewCategory}>Create Article Category</Button></PermissionControl>
                </Flex>
            </div>
            <div className='cnt-item'>
                <Table
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowClassName={'table-row'}
                    size='small'
                    dataSource={tabData}
                    columns={tabCols}
                    pagination={false}
                    expandable={{ rowExpandable: (record) => false, showExpandColumn: false }} />
            </div>
            <div className='footer-part cnt-item'>
                <Pagination
                    total={total}
                    showTotal={(total) => `total ${total}`}
                    current={pageNum}
                    pageSize={pageSize}
                    showSizeChanger={true}
                    pageSizeOptions={[10, 20, 30, 50]}
                    onChange={(page, size) => pageChange(page, size)}
                />
            </div>
        </div>
    )
}

export default AIArticleCategoryScreen;